import { useCallback, useEffect, useState } from "react";
import { NavLink, Route, Routes, useOutletContext, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { getMarketplaceCatalog, getMarketplaceDashboard } from "../../api/clientVendorDirectoryMarketplaceApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.marketplace";

function planDisplayName(plan, locale) {
  if (!plan) return "—";
  if (locale.startsWith("es")) return plan.nameEs || plan.nameEn || plan.planKey;
  return plan.nameEn || plan.nameEs || plan.planKey;
}

function formatMinorAmount(minor, locale, currency = "COP") {
  const amount = Number(minor) || 0;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount / 100);
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function MetricCard({ label, value, hint, tone = "default" }) {
  return (
    <div className={`cvd-metric-card cvd-metric-card--${tone}`}>
      <div className="cvd-metric-card__label">{label}</div>
      <div className="cvd-metric-card__value">{value ?? "—"}</div>
      {hint ? <div className="cvd-metric-card__hint">{hint}</div> : null}
    </div>
  );
}

function MarketplaceShell({ base, children }) {
  const { t } = useLocale();

  return (
    <div className="page-content cvd-admin cvd-marketplace-hub">
      <ClientVendorDirectorySubnav />
      <nav className="cvd-subnav cvd-marketplace-hub__subnav" aria-label={t(`${COPY_PREFIX}.navLabel`)}>
        <NavLink to={base} end>
          {t(`${COPY_PREFIX}.nav.overview`)}
        </NavLink>
        <NavLink to={`${base}/catalogo`}>{t(`${COPY_PREFIX}.nav.catalog`)}</NavLink>
        <NavLink to={`${base}/integridad`}>{t(`${COPY_PREFIX}.nav.integrity`)}</NavLink>
      </nav>
      {children}
    </div>
  );
}

function MarketplaceHeader({ viewName }) {
  const { t } = useLocale();
  return (
    <header className="cvd-marketplace-hub__header">
      <p className="cvd-marketplace-hub__eyebrow">{t(`${COPY_PREFIX}.eyebrow`)}</p>
      <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
      <p className="cvd-admin__subtitle">{t(`${COPY_PREFIX}.intro`, { viewName: viewName || "CasaMapa" })}</p>
    </header>
  );
}

function OverviewPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const base = `${serviciosBase}/marketplace`;
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getMarketplaceDashboard(clientViewId);
      setDashboard(body.dashboard);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = dashboard?.summary || {};
  const queues = dashboard?.queues || {};

  return (
    <MarketplaceShell base={base}>
      <MarketplaceHeader viewName={activeView?.name} />
      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading && dashboard ? (
        <>
          <section className="cvd-marketplace-hub__section" aria-labelledby="marketplace-kpis-title">
            <h2 id="marketplace-kpis-title" className="cvd-marketplace-hub__section-title">
              {t(`${COPY_PREFIX}.overview.kpiTitle`)}
            </h2>
            <p className="cvd-marketplace-hub__section-hint">{t(`${COPY_PREFIX}.overview.kpiHint`)}</p>
            <div className="cvd-metric-grid">
              <MetricCard
                label={t(`${COPY_PREFIX}.metrics.activeSubscriptions`)}
                value={summary.activeSubscriptions}
                hint={t(`${COPY_PREFIX}.metrics.activeSubscriptionsHint`)}
              />
              <MetricCard
                label={t(`${COPY_PREFIX}.metrics.activeCampaigns`)}
                value={summary.activeCampaigns}
                hint={t(`${COPY_PREFIX}.metrics.activeCampaignsHint`)}
              />
              <MetricCard
                label={t(`${COPY_PREFIX}.metrics.availableCredits`)}
                value={summary.availableCredits}
                hint={t(`${COPY_PREFIX}.metrics.availableCreditsHint`)}
              />
              <MetricCard
                label={t(`${COPY_PREFIX}.metrics.openOrders`)}
                value={summary.openOrders}
                hint={t(`${COPY_PREFIX}.metrics.openOrdersHint`)}
              />
              <MetricCard
                label={t(`${COPY_PREFIX}.metrics.pendingRefunds`)}
                value={summary.pendingRefunds}
                hint={t(`${COPY_PREFIX}.metrics.pendingRefundsHint`)}
                tone={summary.pendingRefunds > 0 ? "warning" : "default"}
              />
              <MetricCard
                label={t(`${COPY_PREFIX}.metrics.pendingPayouts`)}
                value={summary.pendingPayouts}
                hint={t(`${COPY_PREFIX}.metrics.pendingPayoutsHint`)}
                tone={summary.pendingPayouts > 0 ? "warning" : "default"}
              />
            </div>
          </section>

          {dashboard.rankingIntegrity?.organicSeparationEnforced ? (
            <div className="cvd-marketplace-hub__integrity-banner" role="status">
              <span className="cvd-marketplace-hub__integrity-icon" aria-hidden="true">
                ✓
              </span>
              <div>
                <strong>{t(`${COPY_PREFIX}.overview.integrityTitle`)}</strong>
                <p>{t(`${COPY_PREFIX}.overview.integrityBody`)}</p>
              </div>
            </div>
          ) : null}

          <div className="cvd-marketplace-hub__split">
            <section className="cvd-marketplace-hub__card" aria-labelledby="marketplace-queues-title">
              <h2 id="marketplace-queues-title">{t(`${COPY_PREFIX}.overview.queuesTitle`)}</h2>
              <ul className="cvd-marketplace-hub__queue-list">
                <li>
                  <span>{t(`${COPY_PREFIX}.overview.campaignReviewQueue`)}</span>
                  <strong>{queues.campaignReview ?? 0}</strong>
                </li>
                <li>
                  <span>{t(`${COPY_PREFIX}.overview.refundQueue`)}</span>
                  <strong>{queues.pendingRefunds ?? summary.pendingRefunds ?? 0}</strong>
                </li>
              </ul>
            </section>

            <section className="cvd-marketplace-hub__card" aria-labelledby="marketplace-orders-title">
              <h2 id="marketplace-orders-title">{t(`${COPY_PREFIX}.overview.recentOrdersTitle`)}</h2>
              {dashboard.recentOrders?.length > 0 ? (
                <div className="cvd-table-wrap">
                  <table className="cvd-table cvd-table--compact">
                    <thead>
                      <tr>
                        <th>{t(`${COPY_PREFIX}.overview.orderStatus`)}</th>
                        <th>{t(`${COPY_PREFIX}.overview.orderTotal`)}</th>
                        <th>{t(`${COPY_PREFIX}.overview.orderCreated`)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.recentOrders.map((order) => (
                        <tr key={order.id}>
                          <td>{t(`${COPY_PREFIX}.orderStatus.${order.status}`, {}, order.status)}</td>
                          <td>{formatMinorAmount(order.total_minor, locale, order.currency || "COP")}</td>
                          <td>{formatDateTime(order.created_at, locale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="empty-state">{t(`${COPY_PREFIX}.overview.recentOrdersEmpty`)}</p>
              )}
            </section>
          </div>
        </>
      ) : null}
    </MarketplaceShell>
  );
}

function CatalogPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const base = `${serviciosBase}/marketplace`;
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getMarketplaceCatalog(clientViewId);
      setCatalog(body.catalog);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.catalogLoadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <MarketplaceShell base={base}>
      <MarketplaceHeader viewName={activeView?.name} />
      <h2 className="cvd-marketplace-hub__section-title">{t(`${COPY_PREFIX}.catalog.title`)}</h2>
      <p className="cvd-marketplace-hub__section-hint">{t(`${COPY_PREFIX}.catalog.intro`)}</p>
      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.catalog.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading && catalog ? (
        <div className="cvd-marketplace-hub__catalog">
          <section className="cvd-marketplace-hub__card">
            <h3>{t(`${COPY_PREFIX}.catalog.plansTitle`)}</h3>
            <div className="cvd-marketplace-hub__plan-grid">
              {catalog.plans?.map((plan) => (
                <article key={plan.planKey} className="cvd-marketplace-hub__plan-card">
                  <header>
                    <h4>{planDisplayName(plan, locale)}</h4>
                    <p className="cvd-marketplace-hub__plan-price">
                      {plan.monthlyPriceMinor > 0
                        ? t(`${COPY_PREFIX}.catalog.priceMonthly`, {
                            price: formatMinorAmount(plan.monthlyPriceMinor, locale),
                          })
                        : t(`${COPY_PREFIX}.catalog.priceFree`)}
                    </p>
                  </header>
                  <ul className="cvd-marketplace-hub__plan-features">
                    <li>
                      {t(`${COPY_PREFIX}.catalog.featurePhotos`, {
                        count: plan.entitlements?.maxPublicPhotos ?? 0,
                      })}
                    </li>
                    <li>
                      {t(`${COPY_PREFIX}.catalog.featureLocations`, {
                        count: plan.entitlements?.maxLocations ?? 0,
                      })}
                    </li>
                    <li>
                      {t(`${COPY_PREFIX}.catalog.featureUsers`, {
                        count: plan.entitlements?.maxVendorUsers ?? 0,
                      })}
                    </li>
                    {plan.entitlements?.quoteSubmission ? (
                      <li>{t(`${COPY_PREFIX}.catalog.featureQuotes`)}</li>
                    ) : null}
                    {plan.entitlements?.featuredPlacementEligible ? (
                      <li>{t(`${COPY_PREFIX}.catalog.featureFeatured`)}</li>
                    ) : null}
                  </ul>
                </article>
              ))}
            </div>
          </section>

          <section className="cvd-marketplace-hub__card">
            <h3>{t(`${COPY_PREFIX}.catalog.creditPacksTitle`)}</h3>
            <div className="cvd-marketplace-hub__plan-grid">
              {catalog.creditPacks?.map((pack) => (
                <article key={pack.packKey} className="cvd-marketplace-hub__plan-card">
                  <header>
                    <h4>{t(`${COPY_PREFIX}.creditPack.${pack.packKey}`, {}, pack.packKey)}</h4>
                    <p className="cvd-marketplace-hub__plan-price">
                      {formatMinorAmount(pack.priceMinor, locale)}
                    </p>
                  </header>
                  <p className="cvd-marketplace-hub__pack-copy">
                    {t(`${COPY_PREFIX}.catalog.creditPackSummary`, {
                      credits: pack.credits,
                      days: pack.expirationDays,
                    })}
                  </p>
                </article>
              ))}
            </div>
          </section>
        </div>
      ) : null}
    </MarketplaceShell>
  );
}

function IntegrityPage() {
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const base = `${serviciosBase}/marketplace`;
  const rules = [
    "organicRanking",
    "topVendor",
    "labeledPlacements",
    "qualityOverrides",
    "billableLeads",
    "paymentData",
  ];

  return (
    <MarketplaceShell base={base}>
      <MarketplaceHeader viewName={activeView?.name} />
      <h2 className="cvd-marketplace-hub__section-title">{t(`${COPY_PREFIX}.integrity.title`)}</h2>
      <p className="cvd-marketplace-hub__section-hint">{t(`${COPY_PREFIX}.integrity.intro`)}</p>
      <ul className="cvd-marketplace-hub__integrity-list">
        {rules.map((rule) => (
          <li key={rule} className="cvd-marketplace-hub__integrity-item">
            <span className="cvd-marketplace-hub__integrity-icon" aria-hidden="true">
              ✓
            </span>
            <div>
              <strong>{t(`${COPY_PREFIX}.integrity.rules.${rule}.title`)}</strong>
              <p>{t(`${COPY_PREFIX}.integrity.rules.${rule}.body`)}</p>
            </div>
          </li>
        ))}
      </ul>
    </MarketplaceShell>
  );
}

export default function ClientVendorDirectoryMarketplaceHubPage() {
  return (
    <Routes>
      <Route index element={<OverviewPage />} />
      <Route path="catalogo" element={<CatalogPage />} />
      <Route path="integridad" element={<IntegrityPage />} />
    </Routes>
  );
}
